/**
 * The spotlight's wire shapes: what the composer (and later the agent) sends,
 * and what a broker's queue carries (spec cova-feed/2026-10-09-001 §5.2–5.3).
 *
 * Text fields carry no schema-level `maxLength`: an @effect/rpc payload-decode
 * failure echoes the rejected value into an untyped defect, so the caps are
 * enforced in the admin service as content-free `FEED_SPOTLIGHT_INVALID`.
 *
 * Frontend-safe.
 */
import { Schema as S } from "effect"
import { PlatformScope } from "@/domain/scope/platform-scope"
import { SpotlightAction } from "./spotlight-action"
import { Highlight, SpotlightHero, SpotlightOverrides } from "./spotlight-content"

/**
 * Who a spotlight is for. Regions and scopes are closed by domain reality (the
 * identity's region, the platform's three surfaces), not by our imagination.
 */
export const SpotlightAudienceRole = S.Literal("everyone", "firm_admins")
export type SpotlightAudienceRole = typeof SpotlightAudienceRole.Type

export const SpotlightRegion = S.Literal("AU", "NZ", "UK")
export type SpotlightRegion = typeof SpotlightRegion.Type
export const SPOTLIGHT_REGIONS: ReadonlyArray<SpotlightRegion> = SpotlightRegion.literals

export const SpotlightScope = PlatformScope
export type SpotlightScope = typeof SpotlightScope.Type

export const SpotlightAudience = S.Struct({
  role: SpotlightAudienceRole,
  regions: S.NonEmptyArray(SpotlightRegion),
  scopes: S.NonEmptyArray(SpotlightScope),
})
export type SpotlightAudience = typeof SpotlightAudience.Type

/** Asked only when a live spotlight's words or picture change. */
export const ReshowChoice = S.Literal("unseen-only", "everyone")
export type ReshowChoice = typeof ReshowChoice.Type

/** How long a spotlight keeps waiting for people who have not signed in, by default. */
export const DEFAULT_RUN_DAYS = 14

export const SpotlightSettingsInput = S.Struct({
  enabled: S.Boolean,
  overrides: SpotlightOverrides,
  audience: SpotlightAudience,
  /** ISO. Null: with the post. */
  startsAt: S.NullOr(S.String),
  /** ISO. Null: never ends. The composer sends start + 14 days by default. */
  endsAt: S.NullOr(S.String),
})
export type SpotlightSettingsInput = typeof SpotlightSettingsInput.Type

/** What a viewer did with a spotlight. A closed set of instrumented moments. */
export const SpotlightSeenOutcome = S.Literal("dismissed", "opened", "requested", "upgraded")
export type SpotlightSeenOutcome = typeof SpotlightSeenOutcome.Type

/** The button as the server resolved it for THIS viewer. Skips never reach the wire. */
export const SpotlightCtaView = S.Union(
  S.Struct({
    kind: S.Literal("open"),
    label: S.String,
    action: SpotlightAction,
    /** The screen's path for a route action; null otherwise. */
    path: S.NullOr(S.String),
  }),
  S.Struct({ kind: S.Literal("request"), label: S.String, note: S.String, destinationId: S.String }),
  S.Struct({
    kind: S.Literal("upgrade"),
    label: S.String,
    note: S.String,
    href: S.String,
    destinationId: S.String,
    featureKey: S.String,
  }),
)
export type SpotlightCtaView = typeof SpotlightCtaView.Type

export const SpotlightView = S.Struct({
  postId: S.String,
  version: S.Int,
  categoryName: S.NullOr(S.String),
  categoryColor: S.NullOr(S.String),
  publishedAt: S.NullOr(S.String),
  hero: SpotlightHero,
  headline: S.String,
  summary: S.String,
  highlights: S.Array(Highlight),
  cta: SpotlightCtaView,
})
export type SpotlightView = typeof SpotlightView.Type

/** At most 5, newest first. */
export const SpotlightQueue = S.Struct({ items: S.Array(SpotlightView) })
export type SpotlightQueue = typeof SpotlightQueue.Type
export const SPOTLIGHT_QUEUE_MAX = 5

/** The Stage's reach line and re-show numbers (super-admin only). */
export const SpotlightStats = S.Struct({
  version: S.Int,
  seenAtVersion: S.Int,
  byOutcome: S.Struct({ dismissed: S.Int, opened: S.Int, requested: S.Int, upgraded: S.Int }),
  seenEarlierVersions: S.Int,
})
export type SpotlightStats = typeof SpotlightStats.Type

export const SpotlightCoachMark = S.NullOr(S.Struct({ destinationId: S.String, version: S.Int }))
export type SpotlightCoachMark = typeof SpotlightCoachMark.Type
